import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";

import { useGetCodeSectionsByIdsQuery } from "graphql/queries/get-code-sections-by-ids.generated";
import { useSearchCodeSectionsQuery } from "graphql/queries/search-code-sections.generated";
import { useGetAppendixCodeSectionsQuery } from "graphql/queries/get-appendix-code-sections.generated";
import { CodeBookType } from "constants/field-values";
import { CODE_SECTION_TEXT_CHARACTER_LIMIT } from "constants/constants";
import { formatSectionNumberForCitation, isAppendixSectionNumber } from "utils/code-section-citation";

/** One citation on a rule or a finding, as the screen already knows it. */
export interface CodeSectionReference {
	code_section_id: string;
	citation_text?: string | null;
	/** Only on a cross-referenced section: the cited sections whose text points to this one,
	 *  e.g. ["IFC 2021 § 507.5.1"]. */
	referenced_by?: string[] | null;
	code_section?: {
		id: string;
		section_number: string;
		title?: string | null;
		page_number?: number | null;
		code_book?: {
			id: string;
			abbreviation: string;
			edition_year?: number | null;
			book_type?: string | null;
			source_file_url?: string | null;
		} | null;
	} | null;
}

/** A section that a rule's cited code refers to, flat, as the backend returns it. */
export interface CrossReferencedCodeSection {
	code_section_id: string;
	section_number: string;
	title?: string | null;
	page_number?: number | null;
	referenced_by: string[];
	code_book: NonNullable<NonNullable<CodeSectionReference["code_section"]>["code_book"]>;
}

// This panel reads a citation with its section nested inside, so the flat cross-referenced
// section is put in that shape. Used by the finding modal and by the rules screen.
export const crossReferenceToCodeSectionReference = (section: CrossReferencedCodeSection): CodeSectionReference => ({
	code_section_id: section.code_section_id,
	referenced_by: section.referenced_by,
	code_section: {
		id: section.code_section_id,
		section_number: section.section_number,
		title: section.title,
		page_number: section.page_number,
		code_book: section.code_book
	}
});

/** The section as the by-ids query returns it, which is all this component renders. */
interface FullSection {
	id: string;
	section_number: string;
	title?: string | null;
	body: string;
	commentary?: string | null;
	page_number?: number | null;
	chapter_number?: string | null;
	chapter_title?: string | null;
	code_book?: {
		id: string;
		abbreviation: string;
		edition_year?: number | null;
		book_type?: string | null;
		source_file_url?: string | null;
	} | null;
}

interface CodeSectionPanelProps {
	references: CodeSectionReference[];
	/** Opens the first section expanded. The report shows one finding at a time, where
	 *  the code text is the point; the rules screen lists many and starts collapsed. */
	defaultExpanded?: boolean;
	emptyLabel?: string;
	/** Leaves out the link to the original code file, for readers who may not be given it. */
	hideSourceLink?: boolean;
}

// Rows fetched for one subtree. Enough for the budget to be what cuts the list.
const SUBTREE_ROW_LIMIT = 500;

interface SubtreeSection {
	id: string;
	section_number: string;
	title?: string | null;
	body?: string | null;
}

// A section heading has no body of its own: the PDF importer leaves it empty, and the
// markdown import wrote the title — or the number, when the heading is untitled — in its
// place. The reader is sent to the provisions under it instead of to its own number.
const isContainer = (section: {
	body?: string | null;
	title?: string | null;
	section_number?: string | null;
}): boolean => {
	const body = (section.body ?? "").trim();
	return !body || body === (section.title ?? "").trim() || body === section.section_number;
};

// The provisions that fit CODE_SECTION_TEXT_CHARACTER_LIMIT, measured the way the service measures
// the text it sends. Sections like IFC 1010 run to fifty provisions and 42,000 characters, and the
// reviewer is shown exactly the part the package was judged against. It stops rather than skipping
// ahead, as the service does.
const withinBudget = (sections: SubtreeSection[]): SubtreeSection[] => {
	const shown: SubtreeSection[] = [];
	let length = 0;
	for (const section of sections) {
		const text = isContainer(section)
			? `${section.section_number} ${section.title ?? ""}`.trim()
			: `${section.section_number} ${section.title ? `${section.title}. ` : ""}${section.body ?? ""}`;
		if (length + text.length > CODE_SECTION_TEXT_CHARACTER_LIMIT) break;
		shown.push(section);
		length += text.length;
	}
	return shown;
};

// The body of a section with text of its own, cut to CODE_SECTION_TEXT_CHARACTER_LIMIT at the last
// paragraph break before the limit. The service cuts the text that it sends in the same way
// (cutLongSectionBody).
const cutLongSectionBody = (body: string): { text: string; isCut: boolean } => {
	if (body.length <= CODE_SECTION_TEXT_CHARACTER_LIMIT) return { text: body, isCut: false };
	const textWithinLimit = body.slice(0, CODE_SECTION_TEXT_CHARACTER_LIMIT);
	const lastParagraphBreak = textWithinLimit.lastIndexOf("\n\n");
	return {
		text: lastParagraphBreak > 0 ? textWithinLimit.slice(0, lastParagraphBreak) : textWithinLimit,
		isCut: true
	};
};

// 'IFC 2021 § 507.5.1' or 'IFC 2021 Appendix D', built from the section rather than from the
// string the rule was written with, so every citation on the screen reads the same way.
const formatCitation = (reference: CodeSectionReference, full?: FullSection): string => {
	const section = full ?? reference.code_section;
	if (!section) return reference.citation_text ?? "Code section";
	const book = section.code_book;
	const edition = [book?.abbreviation, book?.edition_year].filter(Boolean).join(" ");
	return `${edition ? `${edition} ` : ""}${formatSectionNumberForCitation(section.section_number)}`;
};

/**
 * The code language behind a rule, in the screen the reviewer is already on.
 *
 * The text is ours — imported from the published code — so it renders here instead of
 * sending anyone to a 700-page PDF. The link is still offered, opening the original
 * file at the page the section was read from, because a reviewer signing a comment
 * letter checks the source.
 */
const CodeSectionPanel = ({
	references,
	defaultExpanded = false,
	emptyLabel,
	hideSourceLink = false
}: CodeSectionPanelProps) => {
	const ids = useMemo(() => references.map(reference => reference.code_section_id), [references]);

	// The section bodies are a paragraph each and are not carried by the list queries,
	// so they are fetched once for the citations actually on screen.
	const { data, loading } = useGetCodeSectionsByIdsQuery({
		skip: ids.length === 0,
		variables: { ids },
		fetchPolicy: "cache-first"
	});

	const bodies = useMemo(
		() => new Map((data?.code_sections ?? []).map(section => [section.id, section as FullSection])),
		[data]
	);

	if (references.length === 0) {
		return emptyLabel ? <p className="csp-empty">{emptyLabel}</p> : null;
	}

	return (
		<div className="csp-list">
			{references.map((reference, index) => (
				<CodeSectionItem
					key={reference.code_section_id}
					reference={reference}
					full={bodies.get(reference.code_section_id)}
					loading={loading}
					defaultExpanded={defaultExpanded && index === 0}
					hideSourceLink={hideSourceLink}
				/>
			))}
		</div>
	);
};

/**
 * One citation, collapsed until asked for.
 *
 * Each one owns its own subtree query: a rule can cite two whole sections, and a single
 * shared query would leave the second overwriting the first's provisions.
 */
const CodeSectionItem = ({
	reference,
	full,
	loading,
	defaultExpanded,
	hideSourceLink
}: {
	reference: CodeSectionReference;
	full?: FullSection;
	loading: boolean;
	defaultExpanded: boolean;
	hideSourceLink: boolean;
}) => {
	const [isOpen, setIsOpen] = useState(defaultExpanded);

	const summary = reference.code_section;
	const book = full?.code_book ?? summary?.code_book;
	const page = full?.page_number ?? summary?.page_number ?? null;
	const isAmendment = book?.book_type === CodeBookType.AMENDMENT;
	const sectionNumber = full?.section_number ?? summary?.section_number;
	const container = !!full && isContainer(full);
	const isAppendix = isAppendixSectionNumber(sectionNumber);
	const referencedBy = reference.referenced_by ?? [];
	// The title of a whole appendix is "Appendix D", the same text as its citation.
	const sectionTitle = isAppendix ? null : (full?.title ?? summary?.title);

	// A citation to a whole section resolves to its heading, which carries no text: the
	// requirements live in the provisions under it, and those are what the reviewer came
	// to read. Only fetched once such a section is actually open. The sections of an appendix
	// (D101, D102...) do not start with "D.", so an appendix uses its own query.
	const canLoadSubtree = isOpen && container && !!book?.id && !!sectionNumber;
	const { data: numberedSubtreeData, loading: loadingNumberedSubtree } = useSearchCodeSectionsQuery({
		skip: !canLoadSubtree || isAppendix,
		variables: {
			codeBookId: book?.id ?? "",
			search: `${sectionNumber}.%`,
			limit: SUBTREE_ROW_LIMIT
		},
		fetchPolicy: "cache-first"
	});
	const { data: appendixSubtreeData, loading: loadingAppendixSubtree } = useGetAppendixCodeSectionsQuery({
		skip: !canLoadSubtree || !isAppendix,
		variables: {
			codeBookId: book?.id ?? "",
			appendixNumber: sectionNumber ?? "",
			limit: SUBTREE_ROW_LIMIT
		},
		fetchPolicy: "cache-first"
	});
	const subtreeSections = (isAppendix ? appendixSubtreeData : numberedSubtreeData)?.code_sections ?? [];
	const loadingSubtree = isAppendix ? loadingAppendixSubtree : loadingNumberedSubtree;

	return (
		<div className="csp-item">
			<button
				type="button"
				className={`csp-head${isOpen ? " csp-head-open" : ""}`}
				onClick={() => setIsOpen(open => !open)}
			>
				{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
				<BookOpen size={14} />
				<span className="csp-citation">{formatCitation(reference, full)}</span>
				{sectionTitle && (
					<span className={`csp-section-title${isOpen ? " csp-section-title-open" : ""}`}>{sectionTitle}</span>
				)}
				{referencedBy.length > 0 && <span className="csp-referenced-by">via {referencedBy.join(", ")}</span>}
				{isAmendment && <span className="csp-amendment">Local amendment</span>}
			</button>

			{isOpen && (
				<div className="csp-body">
					{loading && !full ? (
						<p className="csp-loading">Loading the code text…</p>
					) : full ? (
						<>
							{container ? (
								<SectionSubtree sections={subtreeSections} loading={loadingSubtree} hideSourceLink={hideSourceLink} />
							) : (
								<SectionBody body={full.body} hideSourceLink={hideSourceLink} />
							)}
							{full.commentary && (
								<div className="csp-commentary">
									<p className="csp-commentary-label">Commentary</p>
									<ReactMarkdown remarkPlugins={[remarkGfm]}>{full.commentary}</ReactMarkdown>
								</div>
							)}
						</>
					) : (
						<p className="csp-loading">This code section is no longer on file.</p>
					)}

					<div className="csp-foot">
						{full?.chapter_number && (
							<span className="csp-chapter">
								Chapter {full.chapter_number}
								{full.chapter_title ? ` — ${full.chapter_title}` : ""}
							</span>
						)}
						{book?.source_file_url && !hideSourceLink && (
							<a
								className="csp-source"
								href={page ? `${book.source_file_url}#page=${page}` : book.source_file_url}
								target="_blank"
								rel="noreferrer"
							>
								{page ? `Open the code at page ${page}` : "Open the code"}
								<ExternalLink size={13} />
							</a>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

/** The text of a section with a body of its own, cut where the analysis cuts it. */
const SectionBody = ({ body, hideSourceLink }: { body: string; hideSourceLink: boolean }) => {
	const { text, isCut } = cutLongSectionBody(body);
	return (
		<>
			<ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
			{isCut && (
				<p className="csp-loading">
					{`Showing the first part of this section, the part the review reads.${hideSourceLink ? "" : " Open the code for the rest."}`}
				</p>
			)}
		</>
	);
};

/** The provisions under a section cited as a whole, in the order the code prints them. */
const SectionSubtree = ({
	sections,
	loading,
	hideSourceLink
}: {
	sections: SubtreeSection[];
	loading: boolean;
	hideSourceLink: boolean;
}) => {
	if (loading && sections.length === 0) return <p className="csp-loading">Loading the code text…</p>;
	if (sections.length === 0) {
		return <p className="csp-loading">This section has no provisions of its own on file.</p>;
	}

	const shown = withinBudget(sections);
	const isCut = shown.length < sections.length || sections.length >= SUBTREE_ROW_LIMIT;

	return (
		<>
			{shown.map(section => (
				<div className="csp-provision" key={section.id}>
					<p className="csp-provision-head">
						{section.section_number}
						{section.title ? ` ${section.title}` : ""}
					</p>
					{/* A subsection heading has no text either; the provisions under it follow. */}
					{!isContainer(section) && <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.body ?? ""}</ReactMarkdown>}
				</div>
			))}
			{isCut && (
				<p className="csp-loading">
					{shown.length === 0
						? `This section is too long to show here.${hideSourceLink ? "" : " Open the code to read it."}`
						: `Showing the first ${shown.length} provisions of this section, the part the review reads.${hideSourceLink ? "" : " Open the code for the rest."}`}
				</p>
			)}
		</>
	);
};

export default CodeSectionPanel;
